import { useMemo } from "react";
import type { Deal } from "@/types/deal.types";
import type { PortfolioEntity, Transaction } from "@/types/portfolio.types";
import { usePortfolioSeedData } from "@/hooks/portfolio/usePortfolioSeedData";
import { computeBalance, computePeriodChange } from "./financials-utils";
import { BalanceOverview } from "./BalanceOverview";
import { CashFlowChart } from "./CashFlowChart";
import { RevenueExpenseBreakdown } from "./RevenueExpenseBreakdown";
import { TransactionLedger } from "./TransactionLedger";
import { ConnectedAccounts } from "./ConnectedAccounts";
import { UpcomingPayments } from "./UpcomingPayments";

type Props = {
  transactions: Transaction[];
  entity: PortfolioEntity | null;
  deals: Deal[];
  dealMap: Record<string, string[]>;
};

export function FinancialsTab({ transactions, entity, deals, dealMap }: Props) {
  const { entityConnectedAccounts: ENTITY_CONNECTED_ACCOUNTS } = usePortfolioSeedData();
  const balance = useMemo(() => computeBalance(transactions), [transactions]);
  const periodChange = useMemo(() => computePeriodChange(transactions, "2026-03-09"), [transactions]);

  const pipelineValue = useMemo(
    () => deals.filter((d) => d.stage !== "complete").reduce((s, d) => s + d.value, 0),
    [deals]
  );

  const pendingInCount = useMemo(
    () => transactions.filter((t) => t.direction === "in" && t.status !== "completed").length,
    [transactions]
  );
  const pendingOutCount = useMemo(
    () => transactions.filter((t) => t.direction === "out" && t.status !== "completed").length,
    [transactions]
  );

  const accounts = useMemo(() => {
    if (!entity) {
      return Object.values(ENTITY_CONNECTED_ACCOUNTS).flat();
    }
    return ENTITY_CONNECTED_ACCOUNTS[entity.id] ?? [];
  }, [entity]);

  const entityName = entity?.name ?? "All Portfolios";

  return (
    <div className="space-y-6">
      <BalanceOverview
        balance={balance.balance}
        pendingIn={balance.pendingIn}
        pendingOut={balance.pendingOut}
        pipelineValue={pipelineValue}
        periodChange={periodChange}
        pendingInCount={pendingInCount}
        pendingOutCount={pendingOutCount}
      />

      <CashFlowChart transactions={transactions} />

      <RevenueExpenseBreakdown transactions={transactions} deals={deals} />

      <TransactionLedger transactions={transactions} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ConnectedAccounts accounts={accounts} entityName={entityName} />
        <UpcomingPayments transactions={transactions} />
      </div>
    </div>
  );
}
